import { useState } from 'react';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandItem, CommandGroup } from '@/components/ui/command';

// Searchable single-select that lets the user ADD a new option inline when their search
// doesn't match anything. The parent decides how new values persist (via onCreate).
export function AddableCombobox({
  value, onChange, options = [], onCreate,
  placeholder = 'Select...', searchPlaceholder = 'Search or type to add...',
  testid = 'combobox', allowClear = true, className = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const q = query.trim();
  const filtered = q ? options.filter(o => o.toLowerCase().includes(q.toLowerCase())) : options;
  const exact = options.some(o => o.toLowerCase() === q.toLowerCase());

  const pick = (val) => { onChange(val); setOpen(false); setQuery(''); };
  const create = async () => {
    if (!q) return;
    if (onCreate) { try { await onCreate(q); } catch { /* best-effort persist */ } }
    pick(q);
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" role="combobox" aria-expanded={open}
          className={`w-full justify-between bg-background border-border font-normal ${value ? '' : 'text-muted-foreground'} ${className}`}
          data-testid={testid}
        >
          <span className="truncate">{value || placeholder}</span>
          <span className="flex items-center gap-1 shrink-0">
            {allowClear && value && (
              <X className="h-3.5 w-3.5 opacity-60 hover:opacity-100" onClick={(e) => { e.stopPropagation(); onChange(''); }} data-testid={`${testid}-clear`} />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-card border-border" align="start" style={{ width: 'var(--radix-popover-trigger-width)' }}>
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} data-testid={`${testid}-input`} />
          <CommandList>
            <CommandGroup>
              {filtered.map(o => (
                <CommandItem key={o} value={o} onSelect={() => pick(o)} data-testid={`${testid}-option-${o}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === o ? 'opacity-100' : 'opacity-0'}`} />
                  {o}
                </CommandItem>
              ))}
              {q && !exact && (
                <CommandItem value={`__add__${q}`} onSelect={create} data-testid={`${testid}-add`}>
                  <Plus className="mr-2 h-4 w-4" /> Add "{q}"
                </CommandItem>
              )}
              {filtered.length === 0 && !q && (
                <div className="px-2 py-3 text-xs text-muted-foreground text-center">Type to search or add</div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default AddableCombobox;
